/**
 * Contract templates (lease PDF templates) — read-only list for the admin
 * contract area.
 *
 * The same rows AdminContractTemplates manages. Here they feed the template
 * picker on a tenant's Contract card, so an admin can see (and preview) which
 * document a contract will be built from before generating a draft.
 *
 * Resolution order mirrors the create-contract edge function exactly, so what
 * the dropdown preselects is what the function would have chosen on its own:
 *   booking.contract_template_id → property.default_contract_template_id
 *   → the is_default row.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ContractTemplateOption = {
  id: string;
  name: string;
  description: string | null;
  template_pdf_url: string;
  is_default: boolean;
};

export function useContractTemplates(enabled: boolean = true) {
  return useQuery<ContractTemplateOption[]>({
    queryKey: ["v2", "contract-templates", "options"],
    enabled,
    // Templates change rarely; keep them warm so opening a tenant profile
    // doesn't refetch on every visit.
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contract_templates")
        .select("id, name, description, template_pdf_url, is_default")
        .order("is_default", { ascending: false })
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as ContractTemplateOption[];
    },
  });
}

/**
 * Which template a contract would actually be built from, given the booking and
 * the property default. Returns null when nothing resolves (the edge function
 * then falls back to its env-configured template or the built-in PDF).
 *
 * A DELIBERATE mirror of create-contract, down to the stale-id case: the
 * function picks ONE candidate id (booking, else property default) and looks it
 * up; a miss drops it straight to the `is_default` row — it never then tries the
 * property default. This used to fall through booking → property → default, so a
 * booking pointing at a deleted template made the dropdown promise the property
 * default while the function built from the system default. Same inputs, same
 * answer, or the preview is describing a different document than the one that
 * would be generated.
 */
export function resolveEffectiveTemplateId(
  templates: ContractTemplateOption[],
  bookingTemplateId?: string | null,
  propertyDefaultTemplateId?: string | null
): string | null {
  const systemDefault = templates.find((t) => t.is_default)?.id ?? null;
  const candidate = bookingTemplateId || propertyDefaultTemplateId || null;
  if (!candidate) return systemDefault;
  return templates.some((t) => t.id === candidate) ? candidate : systemDefault;
}
